import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Field renderers for the contract editor (R2).
 *
 * Each `FieldType` from `../types.ts` has exactly one renderer here.
 * The editor walks the active version's `variableSchema`, looks up the
 * renderer in `RENDERERS`, and produces the input. All renderers share
 * the `FieldRendererProps` shape so the editor wires them generically.
 *
 * Tailwind-styled to match the rest of the SPA. No native browser
 * controls beyond `<input>` / `<textarea>` / `<select>` for now —
 * upgrades to date pickers, currency formatters, etc. can land later.
 *
 * `party_ref` stays a placeholder until R3a builds the contract_parties
 * model and the party picker UI.
 */
import { useEffect, useId, useRef, useState, } from 'react';
import { asDataTableRows, cellValue, formatCell, visibleColumns, } from '../data-table.js';
import { attachmentRefIds, attachmentRefLabel, } from '../atoms/attachment-label.js';
import { DATE_MASK_PLACEHOLDER, isCompleteIsoDate, isIsoDateString, maskDateInput, } from '../date-mask.js';
// ── Shared shell — label + input + help text ──────────────────────────
function FieldShell({ field, children, }) {
    const id = useId();
    return (_jsxs("label", { htmlFor: id, className: "flex flex-col gap-1", children: [_jsxs("span", { className: "text-xs font-medium text-stone-700", children: [field.label, field.requiredAtFinal && _jsx("span", { className: "ml-1 text-red-500", children: "*" })] }), children(id), field.help && _jsx("span", { className: "text-[10px] text-stone-500", children: field.help })] }));
}
const INPUT_CLASSES = 'w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm shadow-sm focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900 disabled:bg-stone-100';
// ── Renderers ─────────────────────────────────────────────────────────
export function TextField(props) {
    return (_jsx(FieldShell, { field: props.field, children: (id) => (_jsx("input", { id: id, type: "text", value: String(props.value ?? ''), onChange: (e) => props.onChange(e.target.value), disabled: props.disabled, className: INPUT_CLASSES })) }));
}
export function TextareaField(props) {
    return (_jsx(FieldShell, { field: props.field, children: (id) => (_jsx("textarea", { id: id, value: String(props.value ?? ''), onChange: (e) => props.onChange(e.target.value), disabled: props.disabled, rows: 3, className: INPUT_CLASSES })) }));
}
function clampNumeric(n, min, max, integer) {
    let r = n;
    if (integer)
        r = Math.round(r);
    if (typeof min === 'number' && r < min)
        r = min;
    if (typeof max === 'number' && r > max)
        r = max;
    return r;
}
export function NumberField(props) {
    const { min, max, integer } = props.field;
    return (_jsx(FieldShell, { field: props.field, children: (id) => (_jsx("input", { id: id, type: "number", min: min, max: max, step: integer ? 1 : undefined, inputMode: integer ? 'numeric' : 'decimal', value: props.value == null ? '' : String(props.value), onChange: (e) => {
                const v = e.target.value;
                if (v === '') {
                    props.onChange(undefined);
                    return;
                }
                const n = Number(v);
                if (Number.isNaN(n))
                    return;
                props.onChange(clampNumeric(n, min, max, integer));
            }, disabled: props.disabled, className: INPUT_CLASSES })) }));
}
export function CurrencyField(props) {
    const { min, max, integer } = props.field;
    return (_jsx(FieldShell, { field: props.field, children: (id) => (_jsxs("div", { className: "flex items-stretch", children: [_jsx("input", { id: id, type: "number", min: min, max: max, step: integer ? 1 : undefined, inputMode: integer ? 'numeric' : 'decimal', value: props.value == null ? '' : String(props.value), onChange: (e) => {
                        const v = e.target.value;
                        if (v === '') {
                            props.onChange(undefined);
                            return;
                        }
                        const n = Number(v);
                        if (Number.isNaN(n))
                            return;
                        props.onChange(clampNumeric(n, min, max, integer));
                    }, disabled: props.disabled, className: INPUT_CLASSES + ' rounded-r-none' }), _jsx("span", { className: "flex items-center rounded-r border border-l-0 border-stone-300 bg-stone-100 px-2 text-xs text-stone-600", children: "SEK" })] })) }));
}
// ── Date entry (T-06 / T-07) ──────────────────────────────────────────
// A masked text input replaces the native `<input type="date">`, whose
// desktop picker has a locale-dependent segment order and fights against
// typing. Here you type 8 digits in ISO order and the dashes auto-insert:
// "20260519" → "2026-05-19". Year → month → day always flows the same way
// regardless of OS locale, and each segment is range-clamped as it fills
// (see `date-mask.ts`). A calendar button on the right still opens the
// OS-native picker for browsing (a hidden native `date` input overlaid on
// the button — no calendar dependency). Storage is unchanged (ISO
// `YYYY-MM-DD` string).
function CalendarIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", width: "15", height: "15", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2" }), _jsx("path", { d: "M16 2v4M8 2v4M3 10h18" })] }));
}
export function DateField(props) {
    const iso = typeof props.value === 'string' ? props.value : '';
    const [text, setText] = useState(() => maskDateInput(iso));
    // What we last pushed up, so we can tell our own onChange echoes apart
    // from genuine external changes (autofill, import, copy-from-contract).
    const lastEmitted = useRef(iso);
    useEffect(() => {
        if (iso !== lastEmitted.current) {
            lastEmitted.current = iso;
            setText(maskDateInput(iso));
        }
    }, [iso]);
    const emit = (next) => {
        if (next === lastEmitted.current)
            return;
        lastEmitted.current = next;
        props.onChange(next);
    };
    // Mask + commit. Only a complete, valid date enters the form model; a
    // half-typed value reads as "no date" downstream but stays on screen.
    const apply = (raw) => {
        const masked = maskDateInput(raw);
        setText(masked);
        emit(isCompleteIsoDate(masked) ? masked : '');
    };
    // At a finished segment (4 or 6 digits, no trailing dash yet) Tab inserts
    // the "-" and holds focus instead of jumping away and dropping the
    // partial entry. A second Tab (the dash is now there) moves on normally.
    const onKeyDown = (e) => {
        if (e.key !== 'Tab' || e.shiftKey)
            return;
        const digits = text.replace(/\D/g, '');
        if ((digits.length === 4 || digits.length === 6) && !text.endsWith('-')) {
            e.preventDefault();
            setText(`${text}-`);
        }
    };
    return (_jsx(FieldShell, { field: props.field, children: (id) => (_jsxs("div", { className: "flex items-stretch", children: [_jsx("input", { id: id, type: "text", inputMode: "numeric", autoComplete: "off", placeholder: DATE_MASK_PLACEHOLDER, value: text, onChange: (e) => apply(e.target.value), onKeyDown: onKeyDown, onBlur: () => setText(maskDateInput(text)), disabled: props.disabled, className: INPUT_CLASSES + ' rounded-r-none' }), _jsxs("label", { title: "V\u00E4lj i kalender", className: 'relative flex items-center rounded-r border border-l-0 border-stone-300 bg-stone-100 px-2 text-stone-600' +
                        (props.disabled ? '' : ' cursor-pointer hover:bg-stone-200'), children: [_jsx(CalendarIcon, {}), _jsx("input", { type: "date", tabIndex: -1, "aria-label": `${props.field.label} – välj datum i kalender`, value: isIsoDateString(iso) ? iso : '', onChange: (e) => apply(e.target.value), disabled: props.disabled, className: "absolute inset-0 cursor-pointer opacity-0 disabled:cursor-default" })] })] })) }));
}
export function BooleanField(props) {
    const id = useId();
    return (_jsxs("label", { htmlFor: id, className: "flex items-start gap-2", children: [_jsx("input", { id: id, type: "checkbox", checked: props.value === true, onChange: (e) => props.onChange(e.target.checked), disabled: props.disabled, className: "mt-0.5" }), _jsxs("span", { className: "flex flex-col gap-0.5", children: [_jsx("span", { className: "text-xs font-medium text-stone-700", children: props.field.label }), props.field.help && (_jsx("span", { className: "text-[10px] text-stone-500", children: props.field.help }))] })] }));
}
export function SelectField(props) {
    const opts = props.field.options ?? [];
    return (_jsx(FieldShell, { field: props.field, children: (id) => (_jsxs("select", { id: id, value: typeof props.value === 'string' ? props.value : '', onChange: (e) => props.onChange(e.target.value), disabled: props.disabled, className: INPUT_CLASSES, children: [_jsx("option", { value: "", disabled: true, children: "V\u00E4lj\u2026" }), opts.map((o) => (_jsx("option", { value: o.value, children: o.label }, o.value)))] })) }));
}
export function MultiSelectField(props) {
    const opts = props.field.options ?? [];
    const selected = Array.isArray(props.value) ? props.value : [];
    const locked = props.lockedOptions;
    return (_jsx(FieldShell, { field: props.field, children: () => (_jsx("div", { className: "flex flex-col gap-1", children: opts.map((o) => {
                // A sibling mutexGroup field already owns this option → lock it
                // here (unless this field is the one that has it checked).
                const owner = locked?.get(o.value);
                const lockedHere = owner !== undefined && !selected.includes(o.value);
                return (_jsxs("label", { className: 'flex items-center gap-2 text-xs ' +
                        (lockedHere ? 'text-stone-400' : ''), title: lockedHere ? `Vald under "${owner}"` : undefined, children: [_jsx("input", { type: "checkbox", checked: selected.includes(o.value), disabled: props.disabled || lockedHere, onChange: (e) => {
                                const next = e.target.checked
                                    ? [...selected, o.value]
                                    : selected.filter((v) => v !== o.value);
                                props.onChange(next);
                            } }), _jsx("span", { children: o.label }), lockedHere && (_jsxs("span", { className: "text-[10px] italic text-stone-400", children: ["vald under \u201C", owner, "\u201D"] }))] }, o.value));
            }) })) }));
}
/**
 * Like Select, plus an "Annat" / "Other" option (sentinel value
 * `__other__`) that reveals a textarea for a custom value.
 * Convention: when the persisted value isn't in the option set, treat
 * it as freetext (i.e. the user previously chose "Annat" and typed in).
 */
export function SelectWithFreetextField(props) {
    const opts = props.field.options ?? [];
    const currentString = typeof props.value === 'string' ? props.value : '';
    const isPredefined = opts.some((o) => o.value === currentString);
    const isFreetext = currentString !== '' && !isPredefined;
    return (_jsx(FieldShell, { field: props.field, children: (id) => (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsxs("select", { id: id, value: isFreetext ? '__other__' : currentString, onChange: (e) => {
                        const v = e.target.value;
                        if (v === '__other__')
                            props.onChange('');
                        else
                            props.onChange(v);
                    }, disabled: props.disabled, className: INPUT_CLASSES, children: [_jsx("option", { value: "", disabled: true, children: "V\u00E4lj\u2026" }), opts.map((o) => (_jsx("option", { value: o.value, children: o.label }, o.value))), _jsx("option", { value: "__other__", children: "Annat (specificera)" })] }), (isFreetext || (currentString === '' && props.value !== undefined)) && (_jsx("textarea", { value: isFreetext ? currentString : '', onChange: (e) => props.onChange(e.target.value), disabled: props.disabled, rows: 2, placeholder: "Beskriv\u2026", className: INPUT_CLASSES }))] })) }));
}
export function PartyRefField(props) {
    return (_jsx(FieldShell, { field: props.field, children: () => (_jsx("div", { className: "rounded border border-dashed border-stone-300 px-2 py-1.5 text-xs text-stone-500", children: "party_ref picker lands in R3a \u2014 leave empty for now" })) }));
}
/**
 * Pattern B in ADR-0028 addendum. Dropdown of the contract's current
 * attachments. Value stored is the attachment id; the body resolves
 * to "Bilaga N" at render time via `attachmentLabel`.
 *
 * Empty attachment list: rendered as a disabled select with a hint
 * pointing the user at the Bilagor tab.
 */
export function AttachmentRefField(props) {
    const field = props.field;
    const attachments = props.attachments ?? [];
    const multiple = field.attachmentMultiple === true;
    const selectedIds = attachmentRefIds(props.value);
    const canCreate = !!props.onCreateAttachment && !props.disabled;
    const emit = (ids) => {
        if (multiple)
            props.onChange(ids);
        else
            props.onChange(ids[0]);
    };
    const create = () => {
        if (!props.onCreateAttachment)
            return;
        void props.onCreateAttachment({
            defaultName: field.attachmentDefaultName,
            recommendedTemplate: field.recommendedAttachmentTemplate,
        }).then((newId) => {
            if (!newId)
                return;
            if (multiple) {
                if (!selectedIds.includes(newId))
                    emit([...selectedIds, newId]);
            }
            else {
                emit([newId]);
            }
        });
    };
    // Single value → require a pick; multi → require at least one.
    const isMissingRequired = selectedIds.length === 0 && field.requiredAtFinal === true;
    const danglingIds = selectedIds.filter((sid) => !attachments.some((a) => a.id === sid));
    // Options the user can still pick (exclude already-selected ones in
    // multi mode so the "add" row only offers new bilagor).
    const pickable = multiple
        ? attachments.filter((a) => !selectedIds.includes(a.id))
        : attachments;
    const CreateButton = canCreate ? (_jsxs("button", { type: "button", onClick: create, className: "flex w-fit items-center gap-1 rounded border border-dashed border-stone-300 px-2 py-1 text-[11px] text-stone-600 hover:border-stone-400 hover:bg-stone-50", children: [_jsx("span", { "aria-hidden": true, children: "+" }), " Skapa bilaga"] })) : null;
    return (_jsx(FieldShell, { field: field, children: (id) => (_jsxs("div", { className: "flex flex-col gap-1.5", children: [multiple && selectedIds.length > 0 && (_jsx("ul", { className: "flex flex-col gap-1", children: selectedIds.map((sid) => {
                        const known = attachments.some((a) => a.id === sid);
                        return (_jsxs("li", { className: "flex items-center justify-between gap-2 rounded border border-stone-200 bg-stone-50 px-2 py-1 text-xs", children: [_jsx("span", { className: known ? 'text-stone-700' : 'text-red-600', children: known
                                        ? attachmentRefLabel(attachments, sid)
                                        : 'Bilaga borttagen' }), !props.disabled && (_jsx("button", { type: "button", onClick: () => emit(selectedIds.filter((x) => x !== sid)), className: "rounded px-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700", title: "Ta bort referens", children: "\u2715" }))] }, sid));
                    }) })), _jsxs("select", { id: id, value: multiple ? '' : (selectedIds[0] ?? ''), onChange: (e) => {
                        const picked = e.target.value;
                        if (multiple) {
                            if (picked && !selectedIds.includes(picked)) {
                                emit([...selectedIds, picked]);
                            }
                        }
                        else {
                            emit(picked ? [picked] : []);
                        }
                    }, disabled: props.disabled || pickable.length === 0, className: INPUT_CLASSES, children: [_jsx("option", { value: "", children: attachments.length === 0
                                ? 'Ingen bilaga finns att välja'
                                : multiple
                                    ? 'Lägg till bilaga…'
                                    : 'Välj bilaga…' }), pickable.map((a) => (_jsx("option", { value: a.id, children: attachmentRefLabel(attachments, a.id) }, a.id)))] }), CreateButton, attachments.length === 0 && (_jsxs("span", { className: "text-[10px] text-stone-500", children: ["V\u00E4lj en befintlig bilaga", canCreate ? ', eller skapa en ny här' : ' under fliken "Bilagor"', "."] })), danglingIds.length > 0 && (_jsx("span", { className: "text-[10px] text-red-600", children: multiple
                        ? 'En eller flera valda bilagor finns inte längre. Ta bort dem och välj på nytt.'
                        : 'Tidigare vald bilaga finns inte längre. Välj en annan.' })), isMissingRequired && (_jsx("span", { className: "text-[10px] text-amber-600", children: "Ingen bilaga vald \u2014 kr\u00E4vs innan avtalet kan markeras klart." }))] })) }));
}
/**
 * Repeating two-column list (`area_list`). Each row is a named space and
 * its area. Stored as `AreaListEntry[]`.
 */
function asAreaList(value) {
    if (!Array.isArray(value))
        return [];
    return value.map((row) => {
        const r = (row ?? {});
        return {
            name: typeof r.name === 'string' ? r.name : '',
            area: typeof r.area === 'string' ? r.area : '',
        };
    });
}
export function AreaListField(props) {
    const rows = asAreaList(props.value);
    const emit = (next) => props.onChange(next);
    const update = (i, patch) => emit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    const add = () => emit([...rows, { name: '', area: '' }]);
    const remove = (i) => emit(rows.filter((_, idx) => idx !== i));
    return (_jsx(FieldShell, { field: props.field, children: () => (_jsxs("div", { className: "flex flex-col gap-1.5", children: [rows.length > 0 && (_jsxs("div", { className: "grid grid-cols-[3fr_1fr_auto] gap-2 text-[10px] font-medium uppercase tracking-wide text-stone-500", children: [_jsx("span", { children: "Utrymme" }), _jsx("span", { children: "Yta" }), _jsx("span", { className: "w-6" })] })), rows.map((row, i) => (_jsxs("div", { className: "grid grid-cols-[3fr_1fr_auto] items-center gap-2", children: [_jsx("input", { type: "text", value: row.name, onChange: (e) => update(i, { name: e.target.value }), disabled: props.disabled, placeholder: "t.ex. F\u00F6rr\u00E5d i k\u00E4llaren", className: INPUT_CLASSES }), _jsx("input", { type: "text", value: row.area, onChange: (e) => update(i, { area: e.target.value }), disabled: props.disabled, placeholder: "t.ex. 8 m\u00B2", className: INPUT_CLASSES }), _jsx("button", { type: "button", onClick: () => remove(i), disabled: props.disabled, className: "w-6 rounded px-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700 disabled:opacity-40", title: "Ta bort rad", children: "\u2715" })] }, i))), !props.disabled && (_jsxs("button", { type: "button", onClick: add, className: "flex w-fit items-center gap-1 rounded border border-dashed border-stone-300 px-2 py-1 text-[11px] text-stone-600 hover:border-stone-400 hover:bg-stone-50", children: [_jsx("span", { "aria-hidden": true, children: "+" }), " L\u00E4gg till utrymme"] }))] })) }));
}
/**
 * Display-only callout — no input, no value. `label` is the heading,
 * `help` the body. Used for in-editor guidance (e.g. "create the
 * avstående as a connected avtal"); gated by `visibleWhen` like any
 * field and never rendered into the document.
 */
export function NoteField(props) {
    const { field } = props;
    return (_jsxs("div", { className: "rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-blue-900", children: [field.label && (_jsx("div", { className: "text-xs font-medium", children: field.label })), field.help && (_jsx("div", { className: `text-[11px] leading-relaxed ${field.label ? 'mt-0.5' : ''}`, children: field.help }))] }));
}
/**
 * Repeating rent-step schedule (`rent_schedule`). Each row is a date the
 * stepped rent takes effect plus the höjningsprocent applied at that step.
 * Stored as `RentScheduleEntry[]`; the kr/månad is derived in the template
 * Body, not here.
 */
function asRentSchedule(value) {
    if (!Array.isArray(value))
        return [];
    return value.map((row) => {
        const r = (row ?? {});
        return {
            date: typeof r.date === 'string' ? r.date : '',
            percent: typeof r.percent === 'number' ? r.percent : null,
            amount: typeof r.amount === 'number' ? r.amount : null,
        };
    });
}
/** Round to whole kronor — rent steps are stated in whole kronor. */
function roundKr(n) {
    return Math.round(n);
}
/** Derive a step's percent from the previous and current amounts. */
function derivePercent(prev, amount) {
    if (prev <= 0)
        return null;
    return Math.round((amount / prev - 1) * 1000) / 10; // one decimal
}
export function RentScheduleField(props) {
    const rows = asRentSchedule(props.value);
    const baseKey = props.field.baseAmountKey;
    const baseVal = baseKey ? props.allValues?.[baseKey] : undefined;
    const base = typeof baseVal === 'number' ? baseVal : null;
    // The amount the row at index `i` steps up FROM: the previous row's
    // amount, or the base move-in rent for the first row.
    const prevAmount = (list, i) => i === 0 ? base : list[i - 1]?.amount ?? null;
    const emit = (next) => props.onChange(next);
    const setDate = (i, date) => emit(rows.map((r, idx) => (idx === i ? { ...r, date } : r)));
    // Set % → derive this row's kr from the previous amount (smart default).
    const setPercent = (i, percent) => emit(rows.map((r, idx) => {
        if (idx !== i)
            return r;
        const prev = prevAmount(rows, i);
        const amount = percent != null && prev != null ? roundKr(prev * (1 + percent / 100)) : r.amount;
        return { ...r, percent, amount };
    }));
    // Set kr → derive this row's % from the previous amount (smart default).
    const setAmount = (i, amount) => emit(rows.map((r, idx) => {
        if (idx !== i)
            return r;
        const prev = prevAmount(rows, i);
        const percent = amount != null && prev != null ? derivePercent(prev, amount) : r.percent;
        return { ...r, amount, percent };
    }));
    const add = () => emit([...rows, { date: '', percent: null, amount: null }]);
    const remove = (i) => emit(rows.filter((_, idx) => idx !== i));
    return (_jsx(FieldShell, { field: props.field, children: () => (_jsxs("div", { className: "flex flex-col gap-1.5", children: [rows.length > 0 && (_jsxs("div", { className: "grid grid-cols-[1.1fr_0.8fr_1fr_auto] gap-2 text-[10px] font-medium uppercase tracking-wide text-stone-500", children: [_jsx("span", { children: "Datum d\u00E5 hyran g\u00E4ller" }), _jsx("span", { children: "H\u00F6jning %" }), _jsx("span", { children: "M\u00E5nadshyra (kr)" }), _jsx("span", { className: "w-6" })] })), rows.map((row, i) => (_jsxs("div", { className: "grid grid-cols-[1.1fr_0.8fr_1fr_auto] items-center gap-2", children: [_jsx("input", { type: "text", inputMode: "numeric", value: maskDateInput(row.date), onChange: (e) => setDate(i, maskDateInput(e.target.value)), onBlur: (e) => setDate(i, maskDateInput(e.target.value)), disabled: props.disabled, placeholder: DATE_MASK_PLACEHOLDER, className: INPUT_CLASSES }), _jsxs("div", { className: "relative", children: [_jsx("input", { type: "number", step: "0.1", value: row.percent == null ? '' : String(row.percent), onChange: (e) => {
                                        const x = e.target.value;
                                        setPercent(i, x === '' ? null : Number.parseFloat(x));
                                    }, disabled: props.disabled, placeholder: "t.ex. 3", className: INPUT_CLASSES + ' pr-6' }), _jsx("span", { className: "pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-stone-400", children: "%" })] }), _jsxs("div", { className: "relative", children: [_jsx("input", { type: "number", step: "1", value: row.amount == null ? '' : String(row.amount), onChange: (e) => {
                                        const x = e.target.value;
                                        setAmount(i, x === '' ? null : Math.round(Number.parseFloat(x)));
                                    }, disabled: props.disabled, placeholder: "t.ex. 12360", className: INPUT_CLASSES + ' pr-7' }), _jsx("span", { className: "pointer-events-none absolute inset-y-0 right-2 flex items-center text-[10px] text-stone-400", children: "kr" })] }), _jsx("button", { type: "button", onClick: () => remove(i), disabled: props.disabled, className: "w-6 rounded px-1 text-stone-400 hover:bg-stone-200 hover:text-stone-700 disabled:opacity-40", title: "Ta bort rad", children: "\u2715" })] }, i))), !props.disabled && (_jsxs("button", { type: "button", onClick: add, className: "flex w-fit items-center gap-1 rounded border border-dashed border-stone-300 px-2 py-1 text-[11px] text-stone-600 hover:border-stone-400 hover:bg-stone-50", children: [_jsx("span", { "aria-hidden": true, children: "+" }), " L\u00E4gg till hyresperiod"] })), _jsx("span", { className: "text-[10px] text-stone-500", children: base == null
                        ? 'Ange inflyttningshyran ovan för att få automatisk beräkning.'
                        : 'Fyll i höjning i procent så fylls månadshyran i kronor i automatiskt — eller tvärtom. Båda går att justera.' })] })) }));
}
/**
 * Free-form tabular data (`data_table`, e.g. a hyreslista). Renders an
 * add/remove-row grid over the field's visible columns. Computed columns
 * (e.g. årshyra slutkund) are read-only and derived per row; columns gated
 * by `visibleWhen` follow the contract's variant via `allValues`. Phase 1:
 * manual entry only — xlsx upload + Bedrock auto-mapping land later and
 * write the same row shape.
 */
export function DataTableField(props) {
    const field = props.field;
    const allCols = field.columns ?? [];
    const cols = visibleColumns(allCols, props.allValues ?? {});
    // Input columns (what the user fills / the xlsx round-trip carries) —
    // computed columns are derived at render, never entered or imported.
    const inputCols = cols.filter((c) => !c.computed);
    const storedRows = asDataTableRows(props.value);
    // Pad the displayed grid up to `minRows` so the user always sees at
    // least the minimum number of rows to fill (e.g. ≥2 mötesförslag). The
    // padding is display-only until edited; the first edit materialises it.
    const minRows = field.minRows ?? 0;
    const rows = storedRows.length < minRows
        ? [
            ...storedRows,
            ...Array.from({ length: minRows - storedRows.length }, () => ({})),
        ]
        : storedRows;
    const xlsx = props.dataTableXlsx;
    const showXlsx = field.xlsxImport !== false;
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const emit = (next) => props.onChange(next);
    const update = (i, key, val) => emit(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
    const add = () => emit([...rows, {}]);
    const remove = (i) => emit(rows.filter((_, idx) => idx !== i));
    const fileName = `${(field.label || 'tabell').replace(/[^a-z0-9åäö-]+/gi, '-')}.xlsx`;
    const onDownload = async () => {
        if (!xlsx)
            return;
        setErr(null);
        try {
            await xlsx.downloadTemplate(inputCols, fileName);
        }
        catch (e) {
            setErr(e.message);
        }
    };
    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !xlsx)
            return;
        setBusy(true);
        setErr(null);
        try {
            const parsed = await xlsx.parseUpload(file, inputCols);
            emit(parsed);
        }
        catch (er) {
            setErr(er.message || 'Kunde inte läsa filen.');
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsx(FieldShell, { field: field, children: () => (_jsxs("div", { className: "flex min-w-0 flex-col gap-2", children: [showXlsx && xlsx && !props.disabled && cols.length > 0 && (_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx("button", { type: "button", onClick: () => void onDownload(), disabled: busy, className: "rounded border border-stone-300 bg-white px-2 py-1 text-[11px] text-stone-700 hover:bg-stone-50 disabled:opacity-50", children: "Ladda ner mall (Excel)" }), _jsxs("label", { className: 'cursor-pointer rounded border border-stone-300 bg-white px-2 py-1 text-[11px] text-stone-700 hover:bg-stone-50 ' +
                                (busy ? 'pointer-events-none opacity-50' : ''), children: [busy ? 'Läser…' : 'Ladda upp Excel', _jsx("input", { type: "file", accept: ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", onChange: (e) => void onUpload(e), className: "hidden", disabled: busy })] }), props.dataTableAutoImport && (_jsx("button", { type: "button", onClick: () => void props.dataTableAutoImport?.(inputCols).then((r) => {
                                if (r)
                                    emit(r);
                            }), disabled: busy, className: "rounded border border-indigo-300 bg-indigo-50 px-2 py-1 text-[11px] text-indigo-800 hover:bg-indigo-100 disabled:opacity-50", title: "Ladda upp valfri Excel \u2014 kolumnerna mappas automatiskt.", children: "Mappa egen Excel automatiskt" })), _jsx("span", { className: "text-[10px] text-stone-400", children: "Uppladdning ers\u00E4tter raderna nedan." })] })), err && (_jsx("p", { className: "rounded border border-red-300 bg-red-50 px-2 py-1 text-[11px] text-red-700", children: err })), cols.length === 0 ? (_jsx("p", { className: "text-[11px] text-stone-500", children: "Inga kolumner definierade." })) : (_jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "w-full border-collapse text-[11px]", children: [_jsx("thead", { children: _jsxs("tr", { children: [cols.map((c) => (_jsx("th", { className: "border border-stone-300 bg-stone-100 px-1.5 py-1 text-left font-medium text-stone-600", style: c.width ? { minWidth: c.width } : undefined, children: c.label }, c.key))), _jsx("th", { className: "w-6 border border-stone-300 bg-stone-100" })] }) }), _jsx("tbody", { children: rows.map((row, i) => (_jsxs("tr", { children: [cols.map((c) => {
                                            if (c.computed) {
                                                return (_jsx("td", { className: "border border-stone-300 bg-stone-50 px-1.5 py-1 text-right font-mono tabular-nums text-stone-600", children: formatCell(c, cellValue(c, row)) }, c.key));
                                            }
                                            return (_jsx("td", { className: "border border-stone-300 p-0", children: _jsx("input", { type: c.kind === 'currency' || c.kind === 'number' ? 'number' : 'text', value: row[c.key] == null ? '' : String(row[c.key]), onChange: (e) => update(i, c.key, e.target.value), disabled: props.disabled, className: "w-full border-0 bg-transparent px-1.5 py-1 text-[11px] focus:bg-amber-50 focus:outline-none disabled:bg-stone-100" }) }, c.key));
                                        }), _jsx("td", { className: "border border-stone-300 text-center", children: _jsx("button", { type: "button", onClick: () => remove(i), disabled: props.disabled, className: "px-1 text-stone-400 hover:text-red-700 disabled:opacity-40", title: "Ta bort rad", children: "\u2715" }) })] }, i))) })] }) })), !props.disabled && (_jsxs("button", { type: "button", onClick: add, className: "flex w-fit items-center gap-1 rounded border border-dashed border-stone-300 px-2 py-1 text-[11px] text-stone-600 hover:border-stone-400 hover:bg-stone-50", children: [_jsx("span", { "aria-hidden": true, children: "+" }), " L\u00E4gg till rad"] }))] })) }));
}
export const RENDERERS = {
    text: TextField,
    textarea: TextareaField,
    number: NumberField,
    currency: CurrencyField,
    date: DateField,
    boolean: BooleanField,
    note: NoteField,
    select: SelectField,
    multi_select: MultiSelectField,
    select_with_freetext: SelectWithFreetextField,
    party_ref: PartyRefField,
    attachment_ref: AttachmentRefField,
    area_list: AreaListField,
    rent_schedule: RentScheduleField,
    data_table: DataTableField,
};
// Visibility evaluation moved to a pure, React-free module (ADR-0049) so
// section-numbering atoms can share it in the PDF render path. Re-exported
// here for back-compat — existing consumers import these from
// '@hyranai/regta-templates'.
export { evaluateVisibilityRule, isFieldVisible } from '../visibility.js';
//# sourceMappingURL=index.js.map